import { useState, type FormEvent } from 'react'
import {
  useCannedReplies,
  useCreateCannedReply,
  useDeleteCannedReply,
  useUpdateCannedReply,
  type CannedReply,
} from '../api/cannedReplies'
import { useMe } from '../api/me'
import { RequestFailed } from '../api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

// The tenant's canned replies. Owners and admins change the list; the
// server refuses everyone else whatever this page shows. Agents see the
// list here read-only so they know which shortcuts exist.
export function CannedRepliesPage() {
  const me = useMe()
  const list = useCannedReplies()
  const create = useCreateCannedReply()
  const [shortcut, setShortcut] = useState('')
  const [body, setBody] = useState('')
  const admin = me.data?.user.role === 'owner' || me.data?.user.role === 'admin'
  const ready = shortcut.trim() !== '' && body.trim() !== ''

  const submit = (e: FormEvent) => {
    e.preventDefault()
    create.mutate(
      { shortcut: shortcut.trim(), body: body.trim() },
      { onSuccess: () => { setShortcut(''); setBody('') } },
    )
  }

  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-6 px-4 py-6 sm:py-8">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Canned replies</h1>
        <p className="text-sm text-muted-foreground">
          Saved answers your team inserts in the inbox by typing / and the shortcut. Write {'{{name}}'} where the customer's name should go. Nothing is sent until someone presses Send.
        </p>
      </div>
      {admin && (
        <form className="flex flex-col gap-3 rounded-xl border bg-card p-4" onSubmit={submit}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="canned-shortcut">Shortcut</Label>
            <div className="flex items-center gap-1">
              <span aria-hidden="true" className="text-muted-foreground">/</span>
              <Input id="canned-shortcut" maxLength={30} placeholder="hours" value={shortcut} onChange={(e) => setShortcut(e.target.value)} className="max-w-xs" />
            </div>
            <p className="text-xs text-muted-foreground">Letters, digits, hyphens and underscores.</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="canned-body">Reply text</Label>
            <Textarea id="canned-body" rows={3} maxLength={4096} placeholder="Hi {{name}}, we are open 9 to 6, Monday to Saturday." value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={!ready || create.isPending}>
              {create.isPending ? 'Saving' : 'Save reply'}
            </Button>
          </div>
          {create.error && <p role="alert" className="text-sm text-destructive">{(create.error as RequestFailed).message}</p>}
        </form>
      )}
      {list.isPending ? (
        <div role="status" aria-label="Loading canned replies" className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : list.isError ? (
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(list.error as RequestFailed).message}</p>
      ) : list.data.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          No canned replies yet. {admin ? 'Save the first one above.' : 'An owner or admin can add them.'}
        </p>
      ) : (
        <ul className="divide-y rounded-xl border bg-card">
          {list.data.map((c) => (
            <Row key={c.id} reply={c} admin={admin} />
          ))}
        </ul>
      )}
    </main>
  )
}

function Row({ reply, admin }: { reply: CannedReply; admin: boolean }) {
  const update = useUpdateCannedReply()
  const del = useDeleteCannedReply()
  const [editing, setEditing] = useState(false)
  const [shortcut, setShortcut] = useState(reply.shortcut)
  const [body, setBody] = useState(reply.body)
  const [confirm, setConfirm] = useState(false)
  const error = (update.error ?? del.error) as RequestFailed | null
  const ready = shortcut.trim() !== '' && body.trim() !== ''

  const cancel = () => {
    setEditing(false)
    setShortcut(reply.shortcut)
    setBody(reply.body)
  }

  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      {editing ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            update.mutate({ id: reply.id, shortcut: shortcut.trim(), body: body.trim() }, { onSuccess: () => setEditing(false) })
          }}
        >
          <div className="flex items-center gap-1">
            <span aria-hidden="true" className="text-muted-foreground">/</span>
            <Input aria-label={`Shortcut for ${reply.shortcut}`} maxLength={30} value={shortcut} onChange={(e) => setShortcut(e.target.value)} autoFocus className="max-w-xs" />
          </div>
          <Textarea aria-label={`Text for ${reply.shortcut}`} rows={3} maxLength={4096} value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={cancel}>Cancel</Button>
            <Button type="submit" disabled={!ready || update.isPending}>Save</Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-semibold">/{reply.shortcut}</code>
            <span className="flex-1" />
            {admin && !confirm && (
              <>
                <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>Edit</Button>
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setConfirm(true)}>Delete</Button>
              </>
            )}
            {admin && confirm && (
              <>
                <span className="text-sm text-muted-foreground">Delete this reply?</span>
                <Button variant="destructive" size="sm" disabled={del.isPending} onClick={() => del.mutate(reply.id)}>Delete</Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>Keep</Button>
              </>
            )}
          </div>
          <p className="whitespace-pre-wrap text-sm text-muted-foreground [overflow-wrap:anywhere]">{reply.body}</p>
        </div>
      )}
      {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
    </li>
  )
}
